import { useNavigate, useOutletContext } from "react-router-dom";
import { UserInterface } from "../../../../core/components/user/domain/interfaces/user_interface";
import { useState, useRef, useCallback, useEffect } from "react";
import ThemeConstants from "../../../../config/theme/theme_constants";
import { useModifyProfile } from "../../domain/hooks/use_modify_profile";
import ErrorDialog from "../../../authentication/presentation/components/error_dialog";
import { PulseLoader } from "react-spinners";
import { useCheckCompanyExists } from "../../../authentication/domain/hooks/company/check_company_exists";
import { processImageFile, validateImageSize } from "../../../../core/utils/image_utils";
import { useCheckEmailAvailable } from "../../../authentication/domain/hooks/auth/check_email_available";
import { toast } from 'sonner';
import { useTranslation } from "../../../../i18n/useTranslation";
import { getCurrentLanguage, setLanguage } from "../../../../i18n/config";
import LanguageSelector from "../../../../core/components/language_selector";

const EditProfilePage: React.FC = () => {
    const { t } = useTranslation();
    const options = [
        { label: t('common.preference.listen'), value: 1 },
        { label: t('common.preference.talk'), value: 2 },
        { label: t('common.preference.both'), value: 3 }
    ];
    const { firstname: initialFirstname, lastname: initialLastname, profilePicture: initialProfilePicture, description: initialDescription, getUserProfile, company, communication: initialCommunication, email, language: initialLanguage } = useOutletContext<UserInterface>();
    const navigate = useNavigate();
    const theme = ThemeConstants();
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    const [ firstname, setFirstname ] = useState<string>(initialFirstname || '');
    const [ lastname, setLastname ] = useState<string>(initialLastname || '');
    const [ communication, setCommunication ] = useState(initialCommunication);
    const [ language, setLanguageValue ] = useState<string>(initialLanguage || getCurrentLanguage());
    const [ pin, setPin ] = useState<string>(company?.pin || '');
    const [ pinError, setPinError ] = useState('');
    const [ lastnameError, setLastnameError ] = useState('');    
    const [ firstnameError, setFirstnameError ] = useState('');
    const [ profilePicture, setProfilePicture ] = useState<string>(initialProfilePicture || '');
    const [ profilePictureFile, setProfilePictureFile ] = useState<File | null>(null);
    const [ description, setDescription ] = useState<string>(initialDescription || '');
    const [ isConvertingImage, setIsConvertingImage ] = useState(false);

    const { mutate: modifyProfile, isPending: isModifyingProfile } = useModifyProfile();
    const { mutate: checkCompany, isPending: isCheckingCompany } = useCheckCompanyExists();
    const { mutate: checkEmailAuthorized, isPending: isCheckingEmail } = useCheckEmailAvailable();

        const handleProfilePictureChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsConvertingImage(true);
        
        try {
            if (!validateImageSize(file, 5)) {
                toast.error(t('profile.edit.file_too_large'));
                setIsConvertingImage(false);
                return;
            }

            const { file: processedFile } = await processImageFile(file);

            setProfilePictureFile(processedFile);
            const imageUrl = URL.createObjectURL(processedFile);
            setProfilePicture(imageUrl);
            
        } catch (error) {
            toast.error(error instanceof Error ? error.message : t('profile.edit.image_error'));
        } finally {
            setIsConvertingImage(false);
        }
    }, [setProfilePicture, setProfilePictureFile, setIsConvertingImage, t]);

    const handleProfilePictureClick = useCallback(() => {
        fileInputRef.current?.click();
    }, [fileInputRef]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (errorDivRef.current && !errorDivRef.current.contains(event.target as Node)) {
                setLastnameError('');
                setFirstnameError('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    
    const handleSubmit = useCallback((e?: React.FormEvent) => {
        e?.preventDefault();

        if (firstname.trim() === '') {
            setFirstnameError(t('common.error.firstname_required'));
        } else if (lastname.trim().length === 0) {
            setLastnameError(t('common.error.lastname_required'));
        } else if (pin.length !== 6) {
            setPinError(t('common.error.code_length'));
        } else if (pinError.length > 0) {
            return;
        } else {
            if (isModifyingProfile === false && isCheckingCompany === false && isCheckingEmail === false) {
                if (
                    firstname !== initialFirstname ||
                    lastname !== initialLastname ||
                    description !== initialDescription ||
                    company?.pin !== pin ||
                    initialCommunication != communication ||
                    (initialLanguage || getCurrentLanguage()) !== language ||
                    profilePictureFile !== null
                ) {
                    modifyProfile({ lastname, firstname, description, picture: profilePictureFile, communication: communication.toLocaleString(), code: pin, language }, {
                        onSuccess: () => {
                            setLanguage(language);
                            toast.success(t('profile.edit.success'));
                            if (getUserProfile) {
                                getUserProfile();
                            }
                        },
                        onError: (error) => {
                            const data = error.response?.data as { message?: string } | undefined;
                            const message = data?.message ?? 'Erreur inconnue';
                            if (message === "Vous ne pouvez pas changer de code entreprise en tant qu'organisateur") {
                                toast.error(t('profile.edit.org_locked', { name: company?.name || t('profile.edit.default_org') }));
                                setPin(company?.pin || '');
                            } else {
                                toast.error(t('profile.edit.error'));
                            }
                        }
                    });
                } else {
                    toast.info(t('profile.edit.no_changes'));
                }
            }
        }
    }, [t, isCheckingEmail, firstname, lastname, description, profilePictureFile, modifyProfile, isModifyingProfile, initialDescription, initialFirstname, initialLastname, getUserProfile, pin, setPinError, setPin, company, pinError, isCheckingCompany, communication, initialCommunication, language, initialLanguage]);

    const errorDivRef = useRef<HTMLDivElement>(null);

    const handlePinChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (pinError) setPinError('');
        const value = e.target.value;
        const filteredValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        if (filteredValue.length === 6) {
            checkCompany(filteredValue, {
                onSuccess: (exists) => {
                    if (exists === false) {
                        setPinError(t('common.error.code_unknown'));
                    } else {
                        checkEmailAuthorized({ email, code: filteredValue }, {
                            onSuccess: (authorization) => {
                                if (authorization === "wrongEmailDomain") {
                                    setPinError(t('profile.edit.email_not_allowed'));
                                }
                            },
                            onError: () => {
                                setPinError(t('common.error.generic_retry'));
                            },
                        });
                    }
                },
                onError: () => {
                    setPinError(t('common.error.generic_retry'));
                },
            });
        }
        setPin(filteredValue);
    }, [pinError, setPinError, setPin, checkCompany, checkEmailAuthorized, email, t]);

    return (
        <div className="min-h-dvh flex flex-col items-center pt-[7.4dvh] pb-[3.7dvh] px-5 w-full justify-between gap-4">
            <div className="w-full relative flex justify-center items-center">
                <button
                    className='absolute left-0 cursor-pointer'
                    onClick={() => navigate(-1)}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <mask id="mask0_2294_7268" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                            <rect width="24" height="24" fill="white"/>
                        </mask>
                        <g mask="url(#mask0_2294_7268)">
                            <path
                                d="M6.8 12.9998L9.7 15.8998C9.88334 16.0831 9.975 16.3165 9.975 16.5998C9.975 16.8831 9.88334 17.1165 9.7 17.2998C9.51667 17.4831 9.28334 17.5748 9 17.5748C8.71667 17.5748 8.48334 17.4831 8.3 17.2998L3.7 12.6998C3.6 12.5998 3.52917 12.4915 3.4875 12.3748C3.44584 12.2581 3.425 12.1331 3.425 11.9998C3.425 11.8665 3.44584 11.7415 3.4875 11.6248C3.52917 11.5081 3.6 11.3998 3.7 11.2998L8.3 6.6998C8.48334 6.51647 8.71667 6.4248 9 6.4248C9.28334 6.4248 9.51667 6.51647 9.7 6.6998C9.88334 6.88314 9.975 7.11647 9.975 7.3998C9.975 7.68314 9.88334 7.91647 9.7 8.0998L6.8 10.9998H20C20.2833 10.9998 20.5208 11.0956 20.7125 11.2873C20.9042 11.479 21 11.7165 21 11.9998C21 12.2831 20.9042 12.5206 20.7125 12.7123C20.5208 12.904 20.2833 12.9998 20 12.9998H6.8Z"
                                fill="black"
                            />
                        </g>
                    </svg>
                </button>
                <h1 className="text-lg font-bold">{t('profile.edit_profile')}</h1>
            </div>
            <form className="flex flex-col w-full items-center max-w-105 gap-4" onSubmit={handleSubmit}>
                <div className='flex flex-col w-full gap-1.25 input-container items-center'>
                    <label className='font-medium text-xs leading-5 self-start'>{t('profile.edit.picture')}</label>
                    <div className='flex flex-col items-center gap-3'>
                        <div 
                            className='w-24 h-24 rounded-full flex items-center justify-center overflow-hidden cursor-pointer'
                            onClick={handleProfilePictureClick}
                        >
                            {profilePicture ? (
                                <img 
                                    src={profilePicture} 
                                    alt={t('profile.edit.picture')}
                                    className='w-full h-full object-cover'
                                />
                            ) : (
                                <svg className='w-8 h-8 text-gray-400' fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                            )}
                        </div>
                        <button
                            type="button"
                            className={`text-xs px-4 py-2 font-medium bg-white rounded-full ${isConvertingImage ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                            onClick={handleProfilePictureClick}
                            disabled={isConvertingImage}
                        >
                            {isConvertingImage ? t('profile.edit.converting') : t('common.button.edit')}
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,.heic,.heif"
                            onChange={handleProfilePictureChange}
                            className='hidden'
                            disabled={isConvertingImage}
                        />
                    </div>
                </div>

                <div className='flex items-center justify-between w-full'>
                    <div className='flex flex-col gap-1.25 input-container' style={{ width: 'calc(50% - 8px)' }}>
                        <label htmlFor="firstname" className='font-medium text-xs leading-5'>{t('common.field.firstname')}</label>
                        <input type="text" id="firstname" name="firstname" className='bg-white rounded-full px-4 py-[10.5px] focus:outline-none w-full' value={firstname} onChange={(e) => setFirstname(e.target.value)}/>
                        {firstnameError && (<ErrorDialog message={firstnameError} ref={errorDivRef}/>)}
                    </div>
                    <div className='flex flex-col gap-1.25 input-container' style={{ width: 'calc(50% - 8px)' }}>
                        <label htmlFor="lastname" className='font-medium text-xs leading-5'>{t('common.field.lastname')}</label>
                        <input type="text" id="lastname" name="lastname" className='bg-white rounded-full px-4 py-[10.5px] focus:outline-none w-full' value={lastname} onChange={(e) => setLastname(e.target.value)}/>
                        {lastnameError && (<ErrorDialog message={lastnameError} ref={errorDivRef}/>)}
                    </div>
                </div>

                <div className='flex flex-col w-full gap-1.25 input-container'>
                    <label htmlFor="description" className='font-medium text-xs leading-5'>{t('profile.edit.card')}</label>
                    <textarea id="description" name="description" className='bg-white rounded-[15px] px-4 py-[10.5px] min-h-50 focus:outline-none resize-none' value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('profile.edit.card_placeholder')}/>
                </div>

                <div className='flex flex-col gap-1.25 input-container w-full items-end'>
                    <div className="flex justify-between items-center w-full">
                        <label htmlFor="pin" className='font-medium text-xs leading-5'>{t('common.field.company_code')}</label>
                        <span className='text-xs'>{pin.length}/6</span>
                    </div>
                    <input 
                        type="text" 
                        id="pin" 
                        name="pin" 
                        maxLength={6} 
                        className='bg-white rounded-full px-4 py-[10.5px] focus:outline-none w-full' 
                        value={pin} 
                        onChange={handlePinChange}
                        disabled={isModifyingProfile}
                    />
                    <span className='text-xs font-medium' style={{ color: theme.UvibesPink }}>{pinError}</span>
                </div>

                <div className='flex flex-col gap-1.25 input-container w-full'>
                    <label className='font-medium text-xs leading-5'>{t('common.field.language')}</label>
                    <LanguageSelector value={language} onChange={setLanguageValue} className='w-full' disabled={isModifyingProfile}/>
                </div>

                <div className='flex flex-col gap-2.25 w-full'>
                    <label htmlFor="communication preferences" className='font-medium text-xs leading-5'>{t('common.preference.label')}</label>
                    {options.map((option) => (
                      <button
                          key={`commucation_preferences_${option.value}`}
                          type="button"
                          disabled={isModifyingProfile}
                          className={`rounded-full w-full font-medium text-black bg-white py-[10.5px] px-9.25 transition-shadow ease-in-out duration-300 cursor-pointer`}
                          style={communication === option.value ? { boxShadow: `inset 0 0 0 1.5px ${theme.UvibesGreen}, 0 0 0 1.5px ${theme.UvibesGreen}` } : {}}
                          onClick={() => setCommunication(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                </div>
            </form>

            <button
                className={`rounded-full text-white font-bold px-9.25 py-2.25 mx-auto ${(isModifyingProfile || isCheckingCompany || isConvertingImage) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                style={{ backgroundColor: theme.UvibesPink }}
                onClick={handleSubmit}
                disabled={isModifyingProfile || isCheckingCompany || isConvertingImage || isCheckingEmail}
            >
                {isModifyingProfile || isCheckingCompany || isConvertingImage || isCheckingEmail ? <PulseLoader speedMultiplier={0.75} size={8} color="white"/> : isConvertingImage ? t('profile.edit.converting_progress') : t('profile.edit_profile')}
            </button>
        </div>
    );
};

export default EditProfilePage;